import React, { useState } from 'react';
import { 
  MapPin, 
  Clock, 
  Phone, 
  Mail, 
  Navigation, 
  ExternalLink, 
  ShieldAlert, 
  CheckCircle2, 
  Copy, 
  Building, 
  UserCheck
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const ContactPage = () => {
  const { lang, isRtl } = useLanguage();
  const { settings, cmsContent } = useStore();
  const [copied, setCopied] = useState(false);

  const addressUrdu = settings.addressUrdu || 'ہمالین پنسار، شاپ 10، رائل پلازہ، نزد کے ایف سی، ای الیون ٹو مرکز اسلام آباد';
  const addressEn = settings.addressEn || 'Himalayan Pansar, Shop 10, Royal Plaza, Near KFC, E-11/2 Markaz, Islamabad';
  const mapsUrl = cmsContent?.socialLinks?.googleMaps || 'https://maps.app.goo.gl/pHyFP7WjwTa3hqis6';
  const clinicTimings = lang === 'ur'
    ? (cmsContent?.footer?.clinicTimingsUrdu || 'روزانہ صبح 10:00 بجے تا شام 8:00 بجے (جمعہ نماز کا وقفہ)')
    : (cmsContent?.footer?.clinicTimingsEn || 'Daily 10:00 AM to 8:00 PM (Friday prayer break)');

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(lang === 'ur' ? addressUrdu : addressEn);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className={`flex-1 py-10 max-w-6xl mx-auto px-4 sm:px-6 w-full animate-fade-in space-y-8 ${lang === 'ur' ? 'font-urdu' : 'font-sans'}`}>
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-950 via-pansar-950 to-emerald-900 text-white rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden text-start">
        <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl">
          <span className="px-3.5 py-1 rounded-full bg-emerald-800/80 text-amber-300 text-xs font-bold inline-block mb-3 border border-emerald-700/60">
            {lang === 'ur' 
              ? `${settings.dawakhanaNameUrdu || 'شیرازی مطب'} • مطب و دواخانہ کا پتہ`
              : `${settings.dawakhanaNameEn || 'Shirazi Clinic'} • Clinic & Store Address`}
          </span>
          <h1 className="text-2xl sm:text-4xl font-black font-title text-white mb-3">
            {lang === 'ur' ? 'رابطہ، مطب کا پتہ اور گوگل لوکیشن' : 'Contact Us, Clinic Address & Map Location'}
          </h1>
          <p className="text-xs sm:text-sm text-emerald-100/90 leading-relaxed max-w-2xl">
            {lang === 'ur'
              ? 'طبی معائنہ، نبض شناسی، بالمشافہ طبی مشورہ اور 100% خالص نباتاتی ادویات حاصل کرنے کے لیے ہمارے مطب تشریف لائیں۔'
              : 'Visit our clinic in Islamabad for clinical pulse examination (Nabs), authentic dietary consultations, and pure herbal formulations.'}
          </p>
        </div>
      </div>

      {/* Important Notice Card */}
      <div className="bg-amber-50/90 border-2 border-amber-300/80 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4 shadow-xs text-start">
        <div className="w-10 h-10 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center shrink-0 shadow-sm mt-0.5">
          <ShieldAlert className="w-5 h-5" />
        </div>
        <div className="space-y-1">
          <h3 className="font-bold text-sm text-amber-950 font-subheading">
            {lang === 'ur' ? 'طبی مشورہ کے لیے اہم ہدایات:' : 'Important Medical Consultation Advisory:'}
          </h3>
          <p className="text-xs text-amber-900 leading-relaxed">
            {lang === 'ur' ? (
              <>
                محترم کسٹمرز و مریض کرام! برائے مہربانی نوٹ فرمائیں کہ <strong>نبض شناسی اور حقیقی طبی تشخیص صرف بالمشافہ ہی ممکن ہے</strong>۔ واٹس ایپ پر بلاوجہ فری چیٹ اور وقت ضائع کرنے سے گریز فرمائیں۔ درست اور محفوظ علاج کے لیے ہمارے مطب تشریف لائیں، یا صرف آرڈر کی تصدیق اور ترسیل کے لیے فون رابطہ فرمائیں۔
              </>
            ) : (
              <>
                Dear Valued Patients & Customers! Please note that <strong>authentic pulse diagnosis and medical examination are only possible in person</strong>. Please avoid requesting diagnostics over free WhatsApp chat. Visit our clinic directly for thorough checkups, or call our phone helpline for order confirmation & dispatch queries.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Address & Details (7 cols) */}
        <div className="lg:col-span-7 space-y-6 text-start">
          
          {/* Card 1: Detailed Physical Address */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-200 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <h2 className="text-lg font-bold text-pansar-900 flex items-center gap-2 font-title">
                <MapPin className="w-5 h-5 text-emerald-700" />
                <span>{lang === 'ur' ? 'دواخانہ و پنسار سٹور کا مکمل پتہ' : 'Pansar Store & Clinic Full Address'}</span>
              </h2>
              <span className="text-xs text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-lg font-bold border border-emerald-200">
                {lang === 'ur' ? 'مرکزی مطب' : 'Main Clinic'}
              </span>
            </div>

            <div className="bg-emerald-50/60 rounded-2xl p-4 sm:p-5 border border-emerald-100 space-y-3">
              <div className="flex items-start gap-3">
                <Building className="w-5 h-5 text-emerald-800 shrink-0 mt-1" />
                <div>
                  <div className="text-base sm:text-lg font-black text-gray-900 leading-snug">
                    {lang === 'ur' ? addressUrdu : addressEn}
                  </div>
                  <div className="text-xs font-sans text-gray-600 mt-1">
                    {lang === 'ur' ? addressEn : addressUrdu}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions for Address */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="py-3 px-6 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs shadow-md transition flex items-center gap-2 cursor-pointer"
              >
                <Navigation className="w-4 h-4 text-amber-300" />
                <span>{lang === 'ur' ? 'گوگل میپس پر لوکیشن کھولیں (Google Maps)' : 'Open in Google Maps'}</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-80" />
              </a>

              <button
                type="button"
                onClick={handleCopyAddress}
                className="py-3 px-5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs transition flex items-center gap-2 cursor-pointer border border-gray-300"
              >
                {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-600" />}
                <span>{copied ? (lang === 'ur' ? 'پتہ کاپی ہو گیا!' : 'Address Copied!') : (lang === 'ur' ? 'پتہ کاپی کریں' : 'Copy Address')}</span>
              </button>
            </div>
          </div>

          {/* Card 2: Timings & Hakim Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            
            {/* Timings */}
            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 space-y-3">
              <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm font-title">
                <Clock className="w-4 h-4" />
                <span>{lang === 'ur' ? 'اوقاتِ مطب (Clinic Timings)' : 'Clinic & Store Timings'}</span>
              </div>
              <p className="text-xs text-gray-700 leading-relaxed">
                {clinicTimings}
              </p>
              <div className="text-[11px] text-gray-500 pt-1 border-t border-gray-100">
                {lang === 'ur' ? 'برائے مہربانی مقررہ اوقات کے دوران تشریف لائیں۔' : 'Please visit during regular business hours.'}
              </div>
            </div>

            {/* Hakim Details */}
            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 space-y-3">
              <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm font-title">
                <UserCheck className="w-4 h-4" />
                <span>{lang === 'ur' ? 'طبی معائنہ کار (Practitioner)' : 'Chief Practitioner'}</span>
              </div>
              <p className="text-xs font-bold text-gray-900 leading-relaxed">
                {lang === 'ur' ? (settings.hakimNameUrdu || 'حکیم مولانا سید عبدالوہاب شاہ شیرازی') : (settings.hakimNameEn || 'Hakeem Syed Abdul Wahab Shah Shirazi')}
              </p>
              <div className="text-[11px] text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block font-bold">
                {lang === 'ur' ? 'فاضل طب والجراحت (FTJ) • ماہر قانون مفرد اعضاء' : 'FTJ (Eastern Medicine & Surgery) • Tibbi Specialist'}
              </div>
            </div>

          </div>

          {/* Card 3: Phone Helpline */}
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{lang === 'ur' ? 'رابطہ و ہیلپ لائن نمبر:' : 'Helpline & Contact Number:'}</span>
                <a href={`tel:${settings.phone}`} className="text-base font-bold font-sans text-pansar-900 hover:text-emerald-700 transition">
                  {settings.phone}
                </a>
              </div>
            </div>

            {settings.email && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-xs text-gray-500 block">{lang === 'ur' ? 'آفیشل ای میل:' : 'Official Email:'}</span>
                  <a href={`mailto:${settings.email}`} className="text-xs font-bold font-sans text-gray-800 hover:text-emerald-700 transition">
                    {settings.email}
                  </a>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Right Column: Interactive Map Preview (5 cols) */}
        <div className="lg:col-span-5 space-y-6 text-start">
          <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-200 space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-sm text-pansar-900 flex items-center gap-1.5 font-subheading">
                <Navigation className="w-4 h-4 text-emerald-700" />
                <span>{lang === 'ur' ? 'گوگل نقشہ (Google Map)' : 'Google Map & Location'}</span>
              </h3>
              <span className="text-[11px] text-gray-500">{lang === 'ur' ? 'اسلام آباد، پاکستان' : 'Islamabad, Pakistan'}</span>
            </div>

            {/* Visual Map Representation */}
            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-inner bg-slate-100 relative group aspect-video sm:aspect-4/3 flex items-center justify-center">
              <iframe
                title="Himalayan Pansar Google Map Location"
                src="https://maps.google.com/maps?q=Himalayan%20Pansar,%20Shop%2010,%20Royal%20Plaza,%20Near%20KFC,%20E-11/2%20Markaz,%20Islamabad&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=&amp;output=embed"
                className="w-full h-full border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 text-xs text-gray-600 flex items-center justify-between">
              <span>{lang === 'ur' ? 'ای الیون ٹو (E-11/2) مرکز، رائل پلازہ' : 'E-11/2 Markaz, Royal Plaza'}</span>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 hover:text-emerald-900 font-bold flex items-center gap-1"
              >
                <span>{lang === 'ur' ? 'براہِ راست گوگل میپس' : 'Direct Google Maps'}</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default ContactPage;

